import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    VoiceConfigData,
    PERSONA_OPTION_TEMPLATES,
    PERSONA_VOICE_MAPPING,
    PersonaOptionTemplate,
} from '../config/voicePersonaConfig';
import { getRealtimeVoiceServerUrl } from '../services/realtimeVoice/config';
import { CANONICAL_PERSONA_ORDER, toCanonicalPersonaId } from '../utils/persona';

// Default configuration (fallback)
const DEFAULT_CONFIG: VoiceConfigData = {
    personas: PERSONA_OPTION_TEMPLATES,
    mappings: PERSONA_VOICE_MAPPING
};

const normalizePersonas = (personas: PersonaOptionTemplate[] | undefined): PersonaOptionTemplate[] => {
    const merged = new Map<string, PersonaOptionTemplate>();

    for (const persona of PERSONA_OPTION_TEMPLATES) {
        merged.set(persona.id, persona);
    }

    for (const persona of personas || []) {
        const canonicalId = toCanonicalPersonaId(persona?.id);
        const base = merged.get(canonicalId) || { id: canonicalId };
        merged.set(canonicalId, {
            ...base,
            ...persona,
            id: canonicalId,
        });
    }

    return CANONICAL_PERSONA_ORDER
        .map((id) => merged.get(id))
        .filter((p): p is PersonaOptionTemplate => Boolean(p));
};

const normalizeMappings = (mappings: Record<string, any> | undefined): typeof PERSONA_VOICE_MAPPING => {
    const normalized: typeof PERSONA_VOICE_MAPPING = {
        ...PERSONA_VOICE_MAPPING,
    };

    if (!mappings) return normalized;

    for (const [personaId, languageMap] of Object.entries(mappings)) {
        const canonicalId = toCanonicalPersonaId(personaId);
        normalized[canonicalId] = {
            ...(normalized[canonicalId] || {}),
            ...(languageMap || {}),
        };
    }

    return normalized;
};

interface VoiceConfigContextType extends VoiceConfigData {
    refreshConfig: () => Promise<void>;
    loading: boolean;
}

const VoiceConfigContext = createContext<VoiceConfigContextType>({
    ...DEFAULT_CONFIG,
    refreshConfig: async () => { },
    loading: false
});

export const useVoiceConfig = () => useContext(VoiceConfigContext);

export const VoiceConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [config, setConfig] = useState<VoiceConfigData>(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(true);

    const fetchConfig = React.useCallback(async () => {
        try {
            // Use the centralized configuration from realtimeVoice to ensure we hit the correct server IP
            // (e.g. 192.168.x.x on device, instead of localhost)
            const wsUrl = getRealtimeVoiceServerUrl();
            const apiUrl = wsUrl
                .replace('ws://', 'http://')
                .replace('wss://', 'https://')
                .replace('/ws/control', '');

            console.log('[VoiceConfig] Fetching config from:', apiUrl);

            const token = localStorage.getItem('jwt_token');
            if (!token) {
                console.warn('[VoiceConfig] No token found, skipping secure config fetch.');
                setLoading(false);
                return;
            }

            const response = await fetch(`${apiUrl}/api/app/config/voice`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                // console.log('[VoiceConfig] Fetched dynamic config:', data);
                setConfig({
                    personas: normalizePersonas(data.personas),
                    mappings: normalizeMappings(data.mappings)
                });
            } else {
                console.warn('[VoiceConfig] Failed to fetch config, using defaults.');
            }
        } catch (error) {
            console.error('[VoiceConfig] Error fetching config:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchConfig();

        // Hot Switch: Auto-refresh when app comes to foreground
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log('[VoiceConfig] App visible, refreshing config...');
                fetchConfig();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchConfig]);

    return (
        <VoiceConfigContext.Provider value={{ ...config, refreshConfig: fetchConfig, loading }}>
            {children}
        </VoiceConfigContext.Provider>
    );
};
